import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useToast } from '@/components/ui/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import { GoogleFitConnect } from './GoogleFitConnect';
import { BluetoothManager } from './BluetoothManager';
import { 
  Settings as SettingsIcon, 
  Bell, 
  Moon, 
  Watch,
  Save,
  Loader2
} from 'lucide-react';
import { Switch } from '@/components/ui/switch';

interface WatchSettings {
  notifications: {
    cycleAlerts: boolean;
    energyAlerts: boolean;
    sleepReminders: boolean;
    heartRateAlerts: boolean;
  };
  thresholds: {
    lowEnergy: number;
    highHeartRate: number;
    optimalSleepDuration: number;
  };
}

const defaultSettings: WatchSettings = {
  notifications: {
    cycleAlerts: true,
    energyAlerts: true,
    sleepReminders: true,
    heartRateAlerts: true
  },
  thresholds: {
    lowEnergy: 30,
    highHeartRate: 120,
    optimalSleepDuration: 480
  }
};

export function Settings() {
  const [settings, setSettings] = useState<WatchSettings>(defaultSettings);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const { currentUser } = useAuth();
  const { toast } = useToast();

  useEffect(() => {
    if (!currentUser) return;

    const loadSettings = async () => {
      try {
        const { data: existingSettings, error: checkError } = await supabase
          .from('watch_settings')
          .select('notifications, thresholds')
          .eq('user_id', currentUser.id)
          .maybeSingle();

        if (checkError && checkError.code !== 'PGRST116') throw checkError;

        if (!existingSettings) {
          const { error: insertError } = await supabase
            .from('watch_settings')
            .upsert({
              user_id: currentUser.id,
              notifications: defaultSettings.notifications,
              thresholds: defaultSettings.thresholds,
              updated_at: new Date().toISOString()
            });

          if (insertError) throw insertError;
          setSettings(defaultSettings);
        } else {
          setSettings({
            notifications: existingSettings.notifications,
            thresholds: existingSettings.thresholds
          });
        }
      } catch (error) {
        console.error('Error loading settings:', error);
        toast({
          variant: "destructive",
          title: "Erreur",
          description: "Impossible de charger les paramètres.",
        });
      } finally {
        setLoading(false);
      }
    };

    loadSettings();
  }, [currentUser, toast]);

  const handleSave = async () => {
    if (!currentUser) return;

    try {
      setSaving(true);
      
      const { error } = await supabase
        .from('watch_settings')
        .upsert({
          user_id: currentUser.id,
          notifications: settings.notifications,
          thresholds: settings.thresholds,
          updated_at: new Date().toISOString()
        });

      if (error) throw error;

      toast({
        title: "Paramètres sauvegardés",
        description: "Vos préférences ont été mises à jour.",
      });
    } catch (error) {
      console.error('Error saving settings:', error);
      toast({
        variant: "destructive",
        title: "Erreur",
        description: "Impossible de sauvegarder les paramètres.",
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center p-4">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Bluetooth Manager */}
      <BluetoothManager />

      {/* Google Fit Connection */}
      <GoogleFitConnect />

      {/* Notification Settings */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Bell className="w-5 h-5 text-blue-600" />
            <h2 className="text-lg font-semibold">Notifications</h2>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">Alertes de cycle</p>
                <p className="text-sm text-gray-500">Notifications de fin de cycle de travail</p>
              </div>
              <Switch
                checked={settings.notifications.cycleAlerts}
                onCheckedChange={(checked) => setSettings(prev => ({
                  ...prev,
                  notifications: { ...prev.notifications, cycleAlerts: checked }
                }))}
              />
            </div>

            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">Alertes d'énergie</p>
                <p className="text-sm text-gray-500">Notifications de baisse d'énergie</p>
              </div>
              <Switch
                checked={settings.notifications.energyAlerts}
                onCheckedChange={(checked) => setSettings(prev => ({
                  ...prev,
                  notifications: { ...prev.notifications, energyAlerts: checked }
                }))}
              />
            </div>

            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">Rappels de sommeil</p>
                <p className="text-sm text-gray-500">Suggestions pour l'heure du coucher</p>
              </div>
              <Switch
                checked={settings.notifications.sleepReminders}
                onCheckedChange={(checked) => setSettings(prev => ({
                  ...prev,
                  notifications: { ...prev.notifications, sleepReminders: checked }
                }))}
              />
            </div>

            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">Alertes cardiaques</p>
                <p className="text-sm text-gray-500">Notifications de fréquence cardiaque élevée</p>
              </div>
              <Switch
                checked={settings.notifications.heartRateAlerts}
                onCheckedChange={(checked) => setSettings(prev => ({
                  ...prev,
                  notifications: { ...prev.notifications, heartRateAlerts: checked }
                }))}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Thresholds Settings */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Watch className="w-5 h-5 text-blue-600" />
            <h2 className="text-lg font-semibold">Seuils</h2>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-6">
            <div>
              <div className="flex justify-between mb-2">
                <label className="font-medium">Seuil d'énergie basse</label>
                <span className="text-sm text-gray-500">{settings.thresholds.lowEnergy}%</span>
              </div>
              <input
                type="range"
                min="10"
                max="50"
                value={settings.thresholds.lowEnergy}
                onChange={(e) => setSettings(prev => ({
                  ...prev,
                  thresholds: { ...prev.thresholds, lowEnergy: parseInt(e.target.value) }
                }))}
                className="w-full"
              />
            </div>

            <div>
              <div className="flex justify-between mb-2">
                <label className="font-medium">Seuil de fréquence cardiaque élevée</label>
                <span className="text-sm text-gray-500">{settings.thresholds.highHeartRate} bpm</span>
              </div>
              <input
                type="range"
                min="100"
                max="180"
                value={settings.thresholds.highHeartRate}
                onChange={(e) => setSettings(prev => ({
                  ...prev,
                  thresholds: { ...prev.thresholds, highHeartRate: parseInt(e.target.value) }
                }))}
                className="w-full"
              />
            </div>

            <div>
              <div className="flex justify-between mb-2">
                <label className="font-medium">Durée de sommeil optimale</label>
                <span className="text-sm text-gray-500">{settings.thresholds.optimalSleepDuration / 60}h</span>
              </div>
              <input
                type="range"
                min="360"
                max="600"
                step="30"
                value={settings.thresholds.optimalSleepDuration}
                onChange={(e) => setSettings(prev => ({
                  ...prev,
                  thresholds: { ...prev.thresholds, optimalSleepDuration: parseInt(e.target.value) }
                }))}
                className="w-full"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Save Button */}
      <Button
        onClick={handleSave}
        disabled={saving}
        className="w-full gap-2"
      >
        {saving ? (
          <>
            <Loader2 className="w-4 h-4 animate-spin" />
            Sauvegarde...
          </>
        ) : (
          <>
            <Save className="w-4 h-4" />
            Sauvegarder les paramètres
          </>
        )}
      </Button>
    </div>
  );
}